import { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { AuthModal } from "@/components/ui/auth-modal";
import { useGenerate } from "@/hooks/use-generation";
import { useUserStore } from "@/stores/user-store";
import { T2V_MODEL, getModelOption, getGenerationCreditCost, INTEGRATED_MODEL_ID, AFFILIATE_DEFAULT_MODEL_ID, INTEGRATED_DURATION_MIN, type ModelId, type DurationOption } from "@/config/models";
import { Loader2, ArrowRight, Upload, X } from "lucide-react";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { UnsupportedFileModal, checkImageFile, unsupportedReasonFromError, IMAGE_ACCEPT, type UnsupportedReason } from "@/components/ui/unsupported-file-modal";
import { IconCoin } from "@/components/ui/cartoon-icons";
import { UpsellModal } from "@/components/ui/upsell-modal";
import { ModelSelector } from "@/components/generate/model-selector";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { trackViewContent, trackImageUploaded, trackCreditsModalShown } from "@/services/analytics";

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

function measureImageFile(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const objUrl = URL.createObjectURL(file);
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(objUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objUrl);
      resolve(null);
    };
    img.src = objUrl;
  });
}

type ImageMeta = { width: number; height: number; megapixels: number };

// Images carried over from an affiliate prefill or a restored draft never go
// through handleUpload, so their dimensions are unknown at generate time — which
// is the majority of first generations. Measure from the URL instead. Capped so
// analytics can never hold up a generation; the bitmap is normally already
// decoded because it is on screen.
function measureImageUrl(url: string, timeoutMs = 500): Promise<ImageMeta | null> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (meta: ImageMeta | null) => {
      if (settled) return;
      settled = true;
      resolve(meta);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    const img = new Image();
    img.onload = () => {
      clearTimeout(timer);
      const { naturalWidth: w, naturalHeight: h } = img;
      done(w && h ? { width: w, height: h, megapixels: (w * h) / 1e6 } : null);
    };
    img.onerror = () => {
      clearTimeout(timer);
      done(null);
    };
    img.src = url;
  });
}

const ASPECT_RATIOS = [
  { value: "16:9", label: "16:9", desc: "Landscape" },
  { value: "9:16", label: "9:16", desc: "Portrait" },
  { value: "1:1", label: "1:1", desc: "Square" },
];

const DRAFT_KEY = "cartoontale_draft_video";

function saveDraft(imageUrl: string, prompt: string) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ imageUrl, prompt }));
  } catch {}
}

function loadDraft(): { imageUrl: string; prompt: string } | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.imageUrl || parsed.prompt)) return parsed;
  } catch {}
  return null;
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch {}
}

// Marks intent-to-generate gated behind signup; on the post-signup return we
// auto-fire the generation so the user never re-clicks Generate.
const AUTOGEN_KEY = "cartoontale_autogen_pending";
function setPendingAutogen() { try { localStorage.setItem(AUTOGEN_KEY, String(Date.now())); } catch {} }
function hasPendingAutogen() {
  try {
    const v = localStorage.getItem(AUTOGEN_KEY);
    const t = v ? Number(v) : 0;
    // Only honor a fresh flag (covers the signup redirect window). Ignore stale
    // flags from a dismissed modal so we never auto-fire an unwanted, credit-
    // spending generation on a later sign-in.
    return t > 0 && Date.now() - t < 15 * 60 * 1000;
  } catch { return false; }
}
function clearPendingAutogen() { try { localStorage.removeItem(AUTOGEN_KEY); } catch {} }

interface PingResponse {
  success: boolean;
  asset_url?: string;
  input_text?: string;
}

// Dedupe the partner-session claim across React StrictMode double-mounts and
// component remounts: one in-flight /ping per cid for the life of the page.
// The backend /ping is idempotent, so a real page refresh (fresh module state)
// simply re-claims the same session and re-issues the cookie.
const pingCache = new Map<string, Promise<PingResponse>>();
function claimPartnerSession(cid: string, fbclid: string | null): Promise<PingResponse> {
  let pending = pingCache.get(cid);
  if (!pending) {
    const params = new URLSearchParams({ cid });
    if (fbclid) params.set("fbclid", fbclid);
    pending = api
      .get<PingResponse>(`/ping?${params.toString()}`)
      .catch(() => ({ success: false }) as PingResponse);
    pingCache.set(cid, pending);
  }
  return pending;
}

export function CreateCartoonPage() {
  const navigate = useNavigate();
  const { user, isSignedIn } = useUser();
  const { credits, hasPurchased, isLoading: isUserLoading } = useUserStore();
  const generateMutation = useGenerate();
  const [searchParams] = useSearchParams();

  const cid = searchParams.get("affiliate_click_id");
  const fbclid = searchParams.get("fbclid");
  const queryPrompt = searchParams.get("prompt") || "";

  const [prefill, setPrefill] = useState<{ imageUrl: string; prompt: string } | null>(null);
  const [affiliateLoading, setAffiliateLoading] = useState(!!cid);

  useEffect(() => {
    if (!cid) return;
    let cancelled = false;

    claimPartnerSession(cid, fbclid)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.asset_url) {
          setPrefill({ imageUrl: res.asset_url, prompt: res.input_text ?? "" });
        }
      })
      .finally(() => { if (!cancelled) setAffiliateLoading(false); });

    return () => { cancelled = true; };
  }, [cid]);

  const affiliateFunnel = !!cid;
  // Free credits only cover Classic Standard 5s — lock every unpurchased user
  // (organic + affiliate) to that model until they buy a pack.
  const freeCreditsLocked = !hasPurchased;
  const hasAffiliatePrefill = !!(prefill?.imageUrl || prefill?.prompt || queryPrompt);
  const draft = hasAffiliatePrefill ? null : loadDraft();

  const [prompt, setPrompt] = useState(prefill?.prompt || queryPrompt || draft?.prompt || "");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [imageUrl, setImageUrl] = useState(prefill?.imageUrl || draft?.imageUrl || "");
  // Free → Classic Standard; purchased → Supercharged (set after store loads).
  const [modelId, setModelId] = useState<ModelId>(AFFILIATE_DEFAULT_MODEL_ID);
  const [duration, setDuration] = useState<DurationOption>("5s");
  const [integratedSeconds, setIntegratedSeconds] = useState(INTEGRATED_DURATION_MIN);
  const [uploading, setUploading] = useState(false);
  const [unsupported, setUnsupported] = useState<UnsupportedReason | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authTitle, setAuthTitle] = useState("Sign Up to Generate");
  const [fakeLoading, setFakeLoading] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [promptEditCount, setPromptEditCount] = useState(0);
  const [imageMeta, setImageMeta] = useState<ImageMeta | null>(null);
  // Whether the image was picked here or arrived with the user. Distinguishes the
  // affiliate/landing funnel from in-app use, which the timing fields cannot do.
  const [imageSource, setImageSource] = useState<"upload" | "carried" | null>(
    prefill?.imageUrl || draft?.imageUrl ? "carried" : null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const purchasedDefaultApplied = useRef(false);
  const formMountedAt = useRef(Date.now());

  const defaultAuthTitle = affiliateFunnel ? "Sign Up to View Your Generation" : "Sign Up to Generate";
  const openAuthModal = useCallback((title?: string) => {
    setPendingAutogen();
    setAuthTitle(title ?? defaultAuthTitle);
    setShowAuth(true);
  }, [defaultAuthTitle]);

  useEffect(() => {
    if (!affiliateFunnel || isSignedIn || affiliateLoading) return;
    if (!hasAffiliatePrefill) return;
    setPendingAutogen();
    setAuthTitle("Sign Up to View Your Generation");
    setFakeLoading(true);
    const timer = setTimeout(() => {
      setFakeLoading(false);
      setShowAuth(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [affiliateFunnel, isSignedIn, affiliateLoading, hasAffiliatePrefill]);

  useEffect(() => {
    if (prefill) {
      // The prefill can resolve after mount, so the initial imageSource guess
      // misses it — mark it here too.
      if (prefill.imageUrl) {
        setImageUrl(prefill.imageUrl);
        setImageSource("carried");
      }
      if (prefill.prompt) setPrompt(prefill.prompt);
    }
  }, [prefill]);

  useEffect(() => {
    if (isUserLoading) return;
    if (freeCreditsLocked) {
      setModelId(AFFILIATE_DEFAULT_MODEL_ID);
      setDuration("5s");
      purchasedDefaultApplied.current = false;
      return;
    }
    if (purchasedDefaultApplied.current) return;
    purchasedDefaultApplied.current = true;
    setModelId(INTEGRATED_MODEL_ID);
  }, [freeCreditsLocked, isUserLoading]);

  useEffect(() => {
    saveDraft(imageUrl, prompt);
  }, [imageUrl, prompt]);

  const hasImage = imageUrl.length > 0;
  const isTextToVideo = !hasImage;
  const effectiveModelId = hasImage && freeCreditsLocked ? AFFILIATE_DEFAULT_MODEL_ID : modelId;
  const activeModel = isTextToVideo
    ? T2V_MODEL
    : (getModelOption(effectiveModelId) ?? getModelOption(AFFILIATE_DEFAULT_MODEL_ID)!);
  const displayCost = getGenerationCreditCost(
    activeModel,
    !isTextToVideo && effectiveModelId === INTEGRATED_MODEL_ID ? integratedSeconds : undefined,
  );
  const canGenerate = prompt.trim().length > 0 && credits >= displayCost;

  useEffect(() => {
    trackViewContent("Create Cartoon", user?.id, { funnel_stage: "create" });
  }, []);

  const triggerUpload = useCallback(() => {
    if (!isSignedIn) { setShowAuth(true); return; }
    fileInputRef.current?.click();
  }, [isSignedIn]);

  const handleUpload = useCallback(async (file: File) => {
    if (!user?.id) { setShowAuth(true); return; }
    const unsupportedReason = checkImageFile(file);
    if (unsupportedReason) { setUnsupported(unsupportedReason); return; }
    setUploading(true);
    try {
      const dims = await measureImageFile(file);
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.upload<{ success: boolean; url: string }>("/upload", formData);
      if (res.url) {
        setImageUrl(res.url);
        setImageSource("upload");
        if (dims) {
          const megapixels = (dims.width * dims.height) / 1e6;
          setImageMeta({ width: dims.width, height: dims.height, megapixels });
          trackImageUploaded({
            user_id: user.id,
            image_width: dims.width,
            image_height: dims.height,
            image_megapixels: megapixels,
            device_type: getDeviceType(),
          });
        } else {
          setImageMeta(null);
        }
      }
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
    }
  }, [user?.id]);

  const handleGenerate = useCallback(async ({ autogen = false }: { autogen?: boolean } = {}) => {
    if (!prompt.trim() || isUserLoading) return;
    // Free affiliate credits only cover the image-to-video path; without an
    // image there's nothing to lock to Classic Standard 5s, so bail out
    // rather than silently falling through to a paid text-to-video request.
    if (affiliateFunnel && freeCreditsLocked && !hasImage) return;

    if (credits < displayCost) {
      trackCreditsModalShown({
        user_id: user?.id,
        source: "generate_insufficient_credits",
        has_purchased: hasPurchased,
      });
      setShowCreditsModal(true);
      return;
    }

    // An autogen run fires from an effect the moment the user lands signed-in, so
    // ms_on_form and prompt_edit_count are near-zero by construction on that path.
    // Send the flag so those fields can be read conditionally rather than being
    // mistaken for a deliberate, fast user.
    const meta = imageMeta ?? (hasImage && imageUrl ? await measureImageUrl(imageUrl) : null);
    if (meta && !imageMeta) setImageMeta(meta);
    try {
      const payload: Record<string, unknown> = hasImage
        ? {
            model_id: effectiveModelId,
            prompt: prompt.trim(),
            reference_image: imageUrl,
          }
        : { model_id: T2V_MODEL.id, prompt: prompt.trim(), aspect_ratio: aspectRatio };

      if (hasImage && effectiveModelId === INTEGRATED_MODEL_ID) {
        payload.duration_seconds = integratedSeconds;
      }

      payload.device_type = getDeviceType();
      payload.ms_on_form = Date.now() - formMountedAt.current;
      payload.prompt_edit_count = promptEditCount;
      payload.had_billing_pageview_before_gen =
        sessionStorage.getItem("visited_billing") === "1";
      payload.is_autogen = autogen;
      payload.image_source = hasImage ? imageSource : null;
      if (meta) {
        payload.image_width = meta.width;
        payload.image_height = meta.height;
        payload.image_megapixels = meta.megapixels;
      }

      const result = await generateMutation.mutateAsync(payload as any);
      if (result.video?.id) {
        clearDraft();
        navigate(`/generation/${result.video.id}`);
      }
    } catch (err) { setUnsupported(unsupportedReasonFromError(err)); console.error("Generation failed:", err); }
  }, [prompt, aspectRatio, imageUrl, hasImage, effectiveModelId, integratedSeconds, displayCost, credits, generateMutation, navigate, isUserLoading, affiliateFunnel, freeCreditsLocked, promptEditCount, imageMeta, imageSource, user?.id, hasPurchased]);

  // Auto-fire the generation after a post-signup redirect so the user doesn't
  // re-click Generate. Once, only when authed, store loaded, and a prompt exists.
  const autoGenFiredRef = useRef(false);
  useEffect(() => {
    if (autoGenFiredRef.current) return;
    if (!isSignedIn || isUserLoading) return;
    if (!hasPendingAutogen() || !prompt.trim()) return;
    autoGenFiredRef.current = true;
    clearPendingAutogen();
    handleGenerate({ autogen: true });
  }, [isSignedIn, isUserLoading, prompt, handleGenerate]);

  const renderGenerateButtons = () => (
    <>
      <Show when="signed-out">
        <div>
          <button
            onClick={() => {
              setPendingAutogen();
              setAuthTitle(defaultAuthTitle);
              setFakeLoading(true);
              setTimeout(() => {
                setFakeLoading(false);
                setShowAuth(true);
              }, 2000);
            }}
            disabled={fakeLoading}
            className="btn-accent w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
          >
            {fakeLoading ? (
              <><Loader2 className="size-4 animate-spin" />{hasImage ? "Animating..." : "Creating..."}</>
            ) : (
              <>{hasImage ? "Animate" : "Generate Video"} <ArrowRight className="size-4" /></>
            )}
          </button>
          {!hasImage && !affiliateFunnel && (
            <p className="flex items-center justify-center gap-1 text-[10px] text-[#241d3a]/45 font-bold mt-1.5">
              <IconCoin className="size-2.5" />{activeModel.creditCost} tokens per video
            </p>
          )}
        </div>
      </Show>

      <Show when="signed-in">
        <div>
          {isUserLoading ? (
            <div className="w-full h-[50px] rounded-2xl bg-[#b8a5f0]/25 animate-pulse" />
          ) : (
            <button
              onClick={() => handleGenerate()}
              disabled={!prompt.trim() || generateMutation.isPending}
              className="btn-accent w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
            >
              {generateMutation.isPending ? (
                <><Loader2 className="size-4 animate-spin" />{hasImage ? "Animating..." : "Creating..."}</>
              ) : (
                <>{hasImage ? "Animate" : "Generate Video"} <ArrowRight className="size-4" /></>
              )}
            </button>
          )}
          {!hasImage && (
            <p className="flex items-center justify-center gap-1 text-[10px] text-[#241d3a]/45 font-bold mt-1.5">
              <IconCoin className="size-2.5" />{activeModel.creditCost} tokens per video
            </p>
          )}
        </div>
      </Show>
    </>
  );

  if (affiliateLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="size-6 animate-spin text-[#7c5cf0]" />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-5 md:px-8 pt-8 pb-16">
      <div className="mb-6">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[#7c5cf0] bg-[#b8a5f0]/15 border border-[#b8a5f0]/30 rounded-full px-2.5 py-1 mb-2">
          Text & Image to Video
        </span>
        <h1 className="text-2xl md:text-3xl font-black text-[#241d3a] tracking-tight mb-1">
          GENERATE VIDEO
        </h1>
        <p className="text-[14px] text-[#241d3a]/60 font-medium">
          {hasImage ? "Animate your image into a video." : "Turn a text prompt or an image into a video clip."}
        </p>
      </div>

      <div className="space-y-4">
        <div
          className={cn(
            "rounded-2xl border-2 p-5 text-center transition-all cursor-pointer",
            hasImage
              ? "border-[#2a2342] bg-white shadow-[2px_2px_0_rgba(42,35,66,0.16)]"
              : "border-dashed border-[#241d3a]/15 bg-[#241d3a]/[0.04] hover:bg-[#241d3a]/[0.08] hover:border-[#241d3a]/25",
          )}
          onClick={() => !hasImage && triggerUpload()}
        >
          {hasImage ? (
            <div className="relative inline-block">
              <img src={imageUrl} alt="Uploaded" className="max-h-48 mx-auto rounded-xl" />
              <button
                onClick={(e) => { e.stopPropagation(); setImageUrl(""); setImageMeta(null); setImageSource(null); }}
                className="absolute -top-2 -right-2 size-6 rounded-full bg-[#241d3a] text-white flex items-center justify-center hover:bg-red-500 transition-colors"
              >
                <X className="size-3" strokeWidth={3} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-2">
              {uploading ? (
                <Loader2 className="size-6 animate-spin text-[#241d3a]/45" />
              ) : (
                <>
                  <Upload className="size-6 text-[#241d3a]/35" />
                  <p className="text-[13px] text-[#241d3a]/45 font-bold">Upload a reference image <span className="text-[#241d3a]/35">(optional)</span></p>
                </>
              )}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
            }}
          />
        </div>

        <textarea
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
            setPromptEditCount((c) => c + 1);
          }}
          placeholder={hasImage
            ? "The character starts walking forward, turns and winks at the camera, colorful background..."
            : "A whimsical town at sunset, colorful houses with exaggerated rooftops, a friendly dragon flying overhead..."}
          rows={4} className="input-field resize-none" />

        {hasImage ? (
          <ModelSelector
            value={modelId}
            duration={duration}
            onChange={setModelId}
            onDurationChange={setDuration}
            integratedSeconds={integratedSeconds}
            onIntegratedSecondsChange={setIntegratedSeconds}
            lockToAffiliateDefault={freeCreditsLocked}
            onRequireAuth={
              isSignedIn ? undefined : () => openAuthModal("Sign Up to Unlock")
            }
          />
        ) : (
          <>
            <div className="flex gap-2.5">
              {ASPECT_RATIOS.map((ar) => (
                <button key={ar.value} onClick={() => setAspectRatio(ar.value)}
                  className={cn(
                    "flex-1 flex flex-col items-center gap-0.5 py-3 rounded-[12px] font-bold transition-all border-2",
                    aspectRatio === ar.value
                      ? "bg-[#b8a5f0] text-[#241d3a] border-[#2a2342] shadow-[2px_2px_0_rgba(42,35,66,0.16)]"
                      : "bg-[#241d3a]/[0.05] text-[#241d3a]/70 border-[#241d3a]/15 hover:bg-[#241d3a]/[0.08] hover:border-[#241d3a]/15",
                  )}>
                  <span className="text-[13px] font-extrabold">{ar.label}</span>
                  <span className={cn("text-[10px]", aspectRatio === ar.value ? "text-[#241d3a]/60" : "text-[#241d3a]/55")}>{ar.desc}</span>
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {["Chase scene", "Magic forest", "Underwater city", "Space adventure", "Fairy tale castle", "Robot dance", "Candy land", "Pirate ship"].map((tag) => (
                <button key={tag} type="button" onClick={() => setPrompt((p) => p ? `${p}, ${tag.toLowerCase()}` : tag.toLowerCase())}
                  className="pill text-[11px] py-1.5 px-3.5 cursor-pointer">
                  {tag}
                </button>
              ))}
            </div>
          </>
        )}

        {isSignedIn && (
          <div className="flex items-center justify-between text-[13px] text-[#241d3a]/60 font-bold pt-1">
            <span className="inline-flex items-center gap-1"><IconCoin className="size-3.5" />{displayCost} tokens{hasImage ? "" : " per video"}</span>
            <span className="tabular-nums">Balance: <span className={credits >= displayCost ? "text-[#241d3a]" : "text-red-500"}>{credits.toLocaleString()}</span></span>
          </div>
        )}

        <div className="hidden md:block">
          {renderGenerateButtons()}
        </div>

        <div className="h-20 md:hidden" aria-hidden="true" />

        {createPortal(
          <div className="md:hidden fixed bottom-0 inset-x-0 z-50">
            <div className="px-5 pt-3 pb-3 bg-[#f3f0fb]/95 backdrop-blur-xl border-t border-[#241d3a]/10">
              {renderGenerateButtons()}
            </div>
            <div className="bg-[#f3f0fb]/95 backdrop-blur-xl" style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
          </div>,
          document.body,
        )}

        {generateMutation.isError && <NsfwOrError error={generateMutation.error} />}
      </div>

      <UnsupportedFileModal reason={unsupported} onClose={() => setUnsupported(null)} onChooseAnother={() => fileInputRef.current?.click()} />
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        title={authTitle}
        previewImageUrl={authTitle === "Sign Up to Unlock" ? null : (imageUrl || null)}
      />

      {/* Out of credits modal */}
      <UpsellModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        creditCost={displayCost}
        credits={credits}
      />

    </div>
  );
}
